import { useLoadingStore } from '@/modules/core/stores/loadingStore'
import { raterGuard } from './guards'
import type { RouteLocationNormalized, NavigationGuardNext, RouteRecordRaw } from 'vue-router'
import { useHealthStore as useOldHealthStore } from '@/modules/core/stores/healthStore'
import { useHealthStore } from '@/modules/health/rater/stores'
import ModifyInsuranceModal from '@/modules/health/rater/views/modals/ModifyInsuranceModal.vue'
import BranchEnum from '@/modules/core/models/BranchEnum'
import HealthInsuranceProcessStepEnum from '@/modules/health/hiring/models/APP/HealthInsuranceProcessStepEnum'

const RATER_TITLE = 'Precios y Tarifas de los Seguros de Salud | ASISA'
const RATER_DESCRIPTION =
  'El mejor precio en tu seguro de salud ASISA con nuestro tarificador. Compara tarifas y encuentra la opción que mejor se adapte a ti.'

export const HEALTH_RATER_ROUTES = [
  {
    path: 'salud',
    name: 'rater-health',
    component: () => import('@/modules/common/rater/views/BaseView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.HEALTH,
      defaultBusinessLineId: 'AS02'
    },
    props: () => ({
      store: useHealthStore()
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing-almost-got-it')
      await raterGuard(to, from, next)
    }
  },
  {
    path: 'salud/:productCode/:clientCode',
    name: 'health-product',
    // component: () => import('@/modules/health/rater/views/ProductBaseView.vue'),
    component: () => import('@/modules/common/rater/views/ProductBaseView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.HEALTH,
      defaultBusinessLineId: 'AS02'
    },
    props: () => ({
      store: useHealthStore(),
      modifyModal: ModifyInsuranceModal,
      omitStopLoading: true
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      await raterGuard(to, from, next)
    }
  },
  {
    path: 'salud/propuestas',
    name: 'health-insurance-proposals',
    component: () => import('@/modules/common/rater/views/ProductProposalsView.vue'),
    meta: {
      title: RATER_TITLE,
      description: RATER_DESCRIPTION,
      branchName: BranchEnum.HEALTH
    },
    props: () => ({
      store: useHealthStore(),
      modifyModal: ModifyInsuranceModal
    }),
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      await raterGuard(to, from, next)
    }
  }
] as RouteRecordRaw[]

export const HEALTH_HIRING_ROUTES = [
  {
    path: 'salud/carencias:pathMatch(.*)',
    name: 'hiring-health-periods',
    component: () => import('@/modules/health/hiring/views/DeficiencyPeriodsView.vue'),
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.HEALTH
    },
    beforeEnter: (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing')
      raterGuard(to, from, next)
    }
  },
  {
    path: 'salud/:id',
    name: 'hiring-health',
    component: () => import('@/modules/health/hiring/views/BaseView.vue'),
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.HEALTH
    },
    beforeEnter: (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext
    ) => {
      useLoadingStore().startLoading('common.loading.processing', 'common.loading.starting-hiring')
      raterGuard(to, from, next)
    }
  },
  {
    path: 'salud/firma/:hash',
    name: 'health-hiring-signature',
    component: () => import('@/modules/common/hiring/views/SignatureView.vue'),
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.HEALTH
    },
    props: {
      branchName: BranchEnum.HEALTH,
      stepCode: HealthInsuranceProcessStepEnum.SIGN
    },
    beforeEnter: () => {
      useLoadingStore().startLoading('common.loading.processing')
    }
  },
  {
    path: 'salud/firma-completada/:hash',
    name: 'health-hiring-signature-done',
    meta: {
      title: 'Seguros Médicos ASISA',
      branchName: BranchEnum.HEALTH
    },
    beforeEnter: () => {
      const loadingStore = useLoadingStore()
      loadingStore.stopLoading()
      loadingStore.startLoading('common.loading.processing')
    },
    component: () => import('@/modules/common/hiring/views/SignatureCompleteView.vue'),
    props: {
      async emissionServiceCallback(quotationId: string) {
        return await useOldHealthStore().emission(quotationId)
      },
      branchName: BranchEnum.HEALTH
    }
  }
] as RouteRecordRaw[]
